/**
 * 3-letter type abbreviations for the compact draft-card BACK layout
 * (refined B1 — see DRAFT_CARD_LAYOUT_HANDOFF.md).
 *
 * The BACK card's move table sits in a ~165 px right column alongside
 * the stats column on the left. A full type chip (e.g. "Electric") at
 * 9 px chars 8 px wide eats ~70 px and forces dropping either PP or
 * Power. Abbreviating to three letters lands every type in ~22 px so
 * the table fits Type / Cat / PP / Pow without compromise.
 *
 * Mapping intent: keep the prefix unique per type (Fir vs Fly vs Fai
 * all distinct) and stay readable to a fan-group player who already
 * knows the 18-type system. Casing is mixed-case (Title) — DM Mono
 * renders ALL CAPS too aggressively for a dense table; mixed-case
 * preserves the 3-letter shape readability.
 *
 * The colour for each abbreviation comes from the matching
 * `--type-{name}` CSS var painted at the call site by DraftScene's
 * patchAsyncTypeData; this module owns only the text mapping.
 */

export const TYPE_ABBREV: Record<string, string> = {
  normal: 'Nrm',
  fire: 'Fir',
  water: 'Wat',
  electric: 'Elc',
  grass: 'Grs',
  ice: 'Ice',
  fighting: 'Fgt',
  poison: 'Psn',
  ground: 'Grd',
  flying: 'Fly',
  psychic: 'Psy',
  bug: 'Bug',
  rock: 'Rck',
  ghost: 'Gho',
  dragon: 'Drg',
  dark: 'Drk',
  steel: 'Stl',
  fairy: 'Fai',
};

/**
 * Returns the 3-letter abbreviation for a Pokémon type. Input may be
 * any case; output is mixed-case (`'Fir'`). Unknown types fall through
 * to the first three characters of the lowercased input title-cased
 * (`'???' → '???'`, `'mystery' → 'Mys'`) so partner data with a typo
 * doesn't crash the card render.
 */
export function typeAbbreviation(type: string): string {
  if (typeof type !== 'string' || type.length === 0) return '—';
  const key = type.toLowerCase();
  const known = TYPE_ABBREV[key];
  if (known !== undefined) return known;
  const slice = key.slice(0, 3);
  return slice.charAt(0).toUpperCase() + slice.slice(1);
}
